import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, ErrorBanner, Field, Input, Stat } from "@/components/ui-kit";
import { BankSelect } from "@/components/BankSelect";
import { useAuth, formatNaira } from "@/lib/auth";
import { useNigerianBanks } from "@/hooks/use-nigerian-banks";
import {
  acceptLoanCounter,
  borrowerCounterOffer,
  declineLoanCounter,
  fetchLoansForBorrower,
  fetchPrimaryBankAccount,
  initiateCreditScoreUnlock,
  initiateRepayment,
  paymentLinkFrom,
  saveDisbursementBankAccount,
  verifyBankAccount,
} from "@/lib/data";
import { supabase } from "@/integrations/supabase/client";
import {
  formatDurationDays,
  isCarryoverEligible,
  isInGrace,
  isVerifiedUpgradePending,
  loanGraceDays,
  maxLoanDaysForLevel,
  maxRolloversForLevel,
  MIN_LOAN_DAYS,
  TIER_LABEL,
  type Loan,
  type LoanStatus,
  type VerificationLevel,
} from "@/lib/types";

export const Route = createFileRoute("/borrower/")({
  component: BorrowerDashboard,
});

const totalDue = (l: Loan) => l.proposedRepay ?? l.amount * (1 + l.interestRate / 100);

function withTier(loan: Loan, borrowerLevel: VerificationLevel): Loan {
  if (loan.verificationLevel && loan.verificationLevel !== "none") return loan;
  if (borrowerLevel === "none") return loan;
  return {
    ...loan,
    verificationLevel: borrowerLevel,
    graceDays: loan.graceDays ?? loanGraceDays({ ...loan, verificationLevel: borrowerLevel }),
  };
}

const PENDING_STATUSES: LoanStatus[] = ["pending", "approved", "countered", "counter_accepted"];

function statusTone(status: LoanStatus): "neutral" | "success" | "warning" | "danger" | "gold" {
  switch (status) {
    case "active":
    case "approved":
    case "counter_accepted":
      return "gold";
    case "repaid":
      return "success";
    case "overdue":
    case "declined":
      return "danger";
    case "pending":
    case "countered":
      return "warning";
    default:
      return "neutral";
  }
}

function statusLabel(status: LoanStatus): string {
  switch (status) {
    case "counter_accepted":
      return "Counter accepted";
    case "countered":
      return "Countered";
    case "pending":
      return "Pending";
    case "approved":
      return "Approved";
    case "active":
      return "Active";
    case "repaid":
      return "Repaid";
    case "overdue":
      return "Overdue";
    case "declined":
      return "Declined";
    default:
      return status;
  }
}

function scoreMeta(s: number) {
  const scale100 = s <= 100;
  if (scale100) {
    if (s >= 81) return { label: "Excellent", color: "text-success", max: 100 };
    if (s >= 61) return { label: "Good", color: "text-gold", max: 100 };
    if (s >= 41) return { label: "Fair", color: "text-warning", max: 100 };
    return { label: "Poor", color: "text-destructive", max: 100 };
  }
  if (s >= 750) return { label: "Excellent", color: "text-success", max: 850 };
  if (s >= 700) return { label: "Good", color: "text-gold", max: 850 };
  if (s >= 600) return { label: "Fair", color: "text-warning", max: 850 };
  return { label: "Poor", color: "text-destructive", max: 850 };
}

function BorrowerDashboard() {
  const { session, refreshBorrower } = useAuth();
  const borrower = session?.kind === "borrower" ? session.borrower : null;
  const [unlocking, setUnlocking] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [actionLoanId, setActionLoanId] = useState<string | null>(null);
  const [counterLoanId, setCounterLoanId] = useState<string | null>(null);
  const [counterPrincipal, setCounterPrincipal] = useState("");
  const [counterRepay, setCounterRepay] = useState("");
  const [counterDays, setCounterDays] = useState("");
  const [counterNote, setCounterNote] = useState("");
  const [counterError, setCounterError] = useState<string | null>(null);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [displayScore, setDisplayScore] = useState(0);
  const [payError, setPayError] = useState<string | null>(null);
  const [needsBank, setNeedsBank] = useState(false);
  const [showKycPrompt, setShowKycPrompt] = useState(false);

  const loadLoans = useCallback(async () => {
    if (!borrower) return;
    try {
      const rows = await fetchLoansForBorrower(borrower.id);
      setLoans(rows);
    } catch (e) {
      console.error(e);
    }
  }, [borrower]);

  const checkBank = useCallback(async () => {
    if (!borrower || borrower.kycStatus !== "approved") {
      setNeedsBank(false);
      return;
    }
    try {
      const ba = await fetchPrimaryBankAccount(borrower.id);
      setNeedsBank(!ba);
    } catch {
      setNeedsBank(true);
    }
  }, [borrower]);

  useEffect(() => {
    loadLoans();
    void checkBank();
  }, [loadLoans, checkBank]);

  useEffect(() => {
    if (!borrower) return;
    if (borrower.kycStatus !== "not_started" && borrower.kycStatus !== "declined") return;
    try {
      if (sessionStorage.getItem("crediking_just_signed_up") === "1") {
        setShowKycPrompt(true);
      }
    } catch {
      /* private mode */
    }
  }, [borrower]);

  useEffect(() => {
    if (!borrower) return;
    const channel = supabase
      .channel(`borrower-loans-${borrower.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "loans", filter: `borrower_id=eq.${borrower.id}` },
        () => {
          loadLoans();
          void refreshBorrower();
        },
      )
      .on("postgres_changes", { event: "*", schema: "public", table: "repayment_schedule" }, () => {
        loadLoans();
      })
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "credit_score_unlocks",
          filter: `user_id=eq.${borrower.id}`,
        },
        () => {
          void refreshBorrower();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [borrower, loadLoans, refreshBorrower]);

  useEffect(() => {
    if (!borrower?.creditScoreUnlocked) {
      setDisplayScore(0);
      return;
    }
    setDisplayScore(0);
    const target = borrower.creditScore;
    const steps = 24;
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setDisplayScore(Math.round((target * i) / steps));
      if (i >= steps) clearInterval(t);
    }, 30);
    return () => clearInterval(t);
  }, [borrower?.creditScore, borrower?.creditScoreUnlocked]);

  if (!borrower) return null;
  const b = borrower;

  // Only active/overdue loans count toward outstanding balance — pending applications are not yet disbursed.
  const activeRaw = loans.find((l) => l.status === "active" || l.status === "overdue");
  const active = activeRaw ? withTier(activeRaw, b.verificationLevel) : undefined;
  const pendingLoans = loans.filter((l) => PENDING_STATUSES.includes(l.status));
  const activeGraceDays = active ? loanGraceDays(active) : 0;
  const activeInGrace = active ? isInGrace(active) : false;
  const activeCarryoverEligible = active ? isCarryoverEligible(active) : false;
  const rolloverMax = active ? maxRolloversForLevel(active.verificationLevel) : 0;

  const kycLabel =
    b.kycStatus === "approved"
      ? "Verified"
      : b.kycStatus === "pending"
        ? "Under review"
        : b.kycStatus === "declined"
          ? "Declined"
          : "Not started";

  const kycTone =
    b.kycStatus === "approved"
      ? "success"
      : b.kycStatus === "pending"
        ? "warning"
        : b.kycStatus === "declined"
          ? "danger"
          : "neutral";

  const unlockScore = async () => {
    setUnlocking(true);
    setPayError(null);
    try {
      const data = await initiateCreditScoreUnlock();
      const link = paymentLinkFrom(data);
      if (!link) throw new Error("No payment link returned.");
      window.open(link, "_blank");
    } catch (e) {
      setPayError(e instanceof Error ? e.message : "Could not start unlock payment.");
    } finally {
      setUnlocking(false);
    }
  };

  const payInstallment = async (loanId: string, amount: number, repaymentId?: string) => {
    setPayingId(repaymentId ?? loanId);
    setPayError(null);
    try {
      const data = await initiateRepayment({ loanId, amount, repaymentId });
      const link = paymentLinkFrom(data);
      if (!link) throw new Error("No payment link returned.");
      window.location.assign(link);
    } catch (e) {
      setPayError(e instanceof Error ? e.message : "Repayment failed.");
    } finally {
      setPayingId(null);
    }
  };

  const acceptCounter = async (loan: Loan) => {
    setActionLoanId(loan.id);
    setPayError(null);
    setCounterError(null);
    try {
      await acceptLoanCounter(loan.id, b.id);
      await loadLoans();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not accept the counter-offer.";
      setPayError(msg);
      setCounterError(msg);
    } finally {
      setActionLoanId(null);
    }
  };

  const declineCounter = async (loan: Loan) => {
    setActionLoanId(loan.id);
    setPayError(null);
    setCounterError(null);
    try {
      await declineLoanCounter(loan.id, b.id);
      await loadLoans();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not decline the counter-offer.";
      setPayError(msg);
      setCounterError(msg);
    } finally {
      setActionLoanId(null);
    }
  };

  const openBorrowerCounter = (loan: Loan) => {
    setCounterLoanId(loan.id);
    setCounterError(null);
    setCounterPrincipal(String(loan.counterPrincipal ?? loan.amount));
    setCounterRepay(String(loan.counterRepay ?? Math.round(totalDue(loan))));
    setCounterDays(String(loan.counterDurationDays ?? loan.durationDays));
    setCounterNote("");
  };

  const submitBorrowerCounter = async (loan: Loan) => {
    const principal = Number(counterPrincipal);
    const repay = Number(counterRepay);
    const days = Number(counterDays);
    if (!principal || principal <= 0) return setCounterError("Enter a valid principal.");
    if (!repay || repay < principal) return setCounterError("Repay must be at least the principal.");
    const maxDays = maxLoanDaysForLevel(b.verificationLevel);
    if (!days || days < MIN_LOAN_DAYS || days > maxDays) {
      return setCounterError(`Duration must be ${MIN_LOAN_DAYS}–${maxDays} days.`);
    }
    setActionLoanId(loan.id);
    setCounterError(null);
    setPayError(null);
    try {
      await borrowerCounterOffer({
        loanId: loan.id,
        borrowerId: b.id,
        principal,
        repay,
        durationDays: days,
        note: counterNote.trim() || undefined,
      });
      setCounterLoanId(null);
      await loadLoans();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not send your counter.";
      setCounterError(msg);
      setPayError(msg);
    } finally {
      setActionLoanId(null);
    }
  };

  const scoreInfo = scoreMeta(b.creditScore || displayScore);

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Welcome back
          </div>
          <h1 className="font-display text-4xl mt-1">
            {b.kycStatus !== "approved" && !isVerifiedUpgradePending(b) && (
              <span className="text-warning text-2xl md:text-3xl font-sans font-medium tracking-normal mr-2 align-middle">
                Unverified
              </span>
            )}
            {b.name.split(" ")[0]}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone={kycTone as never}>KYC · {kycLabel}</Badge>
          {(b.kycStatus === "approved" || isVerifiedUpgradePending(b)) &&
            (b.verificationLevel === "express" ||
              b.verificationLevel === "verified" ||
              b.verificationLevel === "elite") && (
              <Badge tone="gold">{TIER_LABEL[b.verificationLevel]}</Badge>
            )}
          {isVerifiedUpgradePending(b) && (
            <Badge tone="warning">Upgrade to Verified · pending</Badge>
          )}
          {b.kycStatus === "approved" ? (
            <Badge tone="gold">Limit {formatNaira(b.loanLimit)}</Badge>
          ) : (
            <Badge tone="warning">Limit · Pending Approval</Badge>
          )}
        </div>
      </div>

      {payError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {payError}
        </div>
      )}

      {(b.kycStatus === "not_started" || b.kycStatus === "declined") && (
        <Card className="border-gold/40 bg-gold/5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <Badge tone={b.kycStatus === "declined" ? "danger" : "warning"}>
                {b.kycStatus === "declined" ? "KYC declined" : "Action required"}
              </Badge>
              <h2 className="font-display text-xl mt-2">
                {b.kycStatus === "declined" ? "Resubmit your KYC" : "Complete your KYC"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                {b.kycStatus === "declined"
                  ? b.kyc?.declineReason
                    ? `Reason: ${b.kyc.declineReason}`
                    : "Update your details and documents so we can re-review your account."
                  : "Verify your identity to unlock your loan limit. This only takes a few minutes."}
              </p>
            </div>
            <Button variant="gold" asChild>
              <Link to="/borrower/apply" search={{ flow: "kyc" }}>
                {b.kycStatus === "declined" ? "Resubmit KYC" : "Complete KYC"}
              </Link>
            </Button>
          </div>
        </Card>
      )}

      {b.kycStatus === "pending" && (
        <Card>
          <Badge tone="warning">Under review</Badge>
          <h2 className="font-display text-xl mt-2">
            {isVerifiedUpgradePending(b) ? "Verified upgrade under review" : "KYC submitted"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {isVerifiedUpgradePending(b)
              ? "We've received your upgrade request. You can't submit again until we approve or decline it."
              : "We're reviewing your documents. You'll be notified as soon as possible once your limit is set."}
          </p>
        </Card>
      )}

      {b.kycStatus === "approved" && b.verificationLevel === "express" && !isVerifiedUpgradePending(b) && (
        <Card className="border-gold/30">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <Badge tone="gold">{TIER_LABEL.express}</Badge>
              <h2 className="font-display text-xl mt-2">Upgrade to Verified</h2>
              <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                {b.kyc?.declineReason
                  ? `Previous upgrade was declined: ${b.kyc.declineReason}. Fix the issues and apply again.`
                  : "Add salary-job proof, next of kin, and a reachable guarantor for higher limits and the ability to negotiate your own interest rate."}
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/borrower/apply" search={{ flow: "kyc" }}>
                Upgrade
              </Link>
            </Button>
          </div>
        </Card>
      )}

      {needsBank && (
        <AddBankCard
          userId={b.id}
          onSaved={() => {
            setNeedsBank(false);
          }}
        />
      )}

      {showKycPrompt && (
        <KycWelcomeModal
          onStart={() => {
            sessionStorage.removeItem("crediking_just_signed_up");
            setShowKycPrompt(false);
          }}
          onLater={() => {
            sessionStorage.removeItem("crediking_just_signed_up");
            setShowKycPrompt(false);
          }}
        />
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label="Loan limit"
          value={b.kycStatus === "approved" ? formatNaira(b.loanLimit) : "—"}
          sub={b.kycStatus !== "approved" ? "Set after KYC review" : "Available"}
        />
        <Stat
          label="Active balance"
          value={active ? formatNaira(totalDue(active) - active.paid) : "—"}
          sub={active ? `Loan ${active.id.slice(0, 8).toUpperCase()}` : "No active loan"}
        />
        <Stat
          label="Loans taken"
          value={loans.length}
          sub={`${loans.filter((l) => l.status === "repaid").length} repaid`}
        />
        <Stat
          label="On-time rate"
          value={
            loans.length
              ? `${Math.round((loans.filter((l) => l.status === "repaid").length / loans.length) * 100)}%`
              : "—"
          }
          sub="Lifetime"
        />
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Active loan</h2>
            {active && <Badge tone={statusTone(active.status)}>{statusLabel(active.status)}</Badge>}
          </div>
          {!active ? (
            <p className="mt-4 text-sm text-muted-foreground">You don't have an active loan.</p>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-xs text-muted-foreground">Outstanding</div>
                  <div className="font-display text-4xl text-gold">
                    {formatNaira(totalDue(active) - active.paid)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Due</div>
                  <div className="font-display text-lg">
                    {new Date(active.dueDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div>
                <div className="h-2 rounded-full bg-accent overflow-hidden">
                  <div
                    className="h-full bg-gradient-gold"
                    style={{ width: `${(active.paid / Math.max(1, totalDue(active))) * 100}%` }}
                  />
                </div>
                <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                  <span>Paid {formatNaira(active.paid)}</span>
                  <span>Total {formatNaira(totalDue(active))}</span>
                </div>
              </div>
              {activeInGrace && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                  Past due — you're in the {activeGraceDays}-day grace period
                  {rolloverMax === 0
                    ? "."
                    : " before a rollover may be requested."}
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <Button variant="gold" asChild>
                  <Link to="/borrower/repay">Make a repayment</Link>
                </Button>
                {activeCarryoverEligible && (
                  <Button variant="outline" asChild>
                    <Link to="/borrower/repay">Rollover on Repay</Link>
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        <Card className="relative overflow-hidden">
          <h2 className="font-display text-xl">Credit score</h2>
          {b.creditScoreUnlocked ? (
            <div className="mt-6">
              <div className={`font-display text-6xl ${scoreInfo.color}`}>{displayScore}</div>
              <div className={`mt-1 text-sm ${scoreInfo.color}`}>
                {scoreInfo.label} · out of {scoreInfo.max}
              </div>
              <div className="mt-6 h-2 rounded-full bg-accent overflow-hidden">
                <div
                  className="h-full bg-gradient-gold"
                  style={{ width: `${Math.min(100, (displayScore / scoreInfo.max) * 100)}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
                <span>Poor</span>
                <span>Fair</span>
                <span>Good</span>
                <span>Excellent</span>
              </div>
            </div>
          ) : (
            <div className="mt-6 relative">
              <div className="select-none pointer-events-none blur-md opacity-60">
                <div className="font-display text-6xl text-gold">●●●</div>
                <div className="mt-1 text-sm text-muted-foreground">out of {scoreInfo.max}</div>
                <div className="mt-6 h-2 rounded-full bg-accent overflow-hidden">
                  <div className="h-full bg-gradient-gold w-3/4" />
                </div>
              </div>
              <div className="absolute inset-0 grid place-items-center">
                <div className="text-center">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">
                    Locked
                  </div>
                  <Button variant="gold" loading={unlocking} onClick={unlockScore} className="mt-3">
                    Unlock My Score — ₦1,000
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card>
        <h2 className="font-display text-xl">Pending applications</h2>
        {pendingLoans.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            You have no pending loan applications.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {pendingLoans.map((loan) => (
              <div key={loan.id} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-display text-lg">{formatNaira(loan.amount)}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDurationDays(loan.durationDays, { capAt30: false })} · repay {formatNaira(totalDue(loan))}
                    </div>
                  </div>
                  <Badge tone={statusTone(loan.status)}>{statusLabel(loan.status)}</Badge>
                </div>

                {loan.status === "countered" && (
                  <div className="mt-3 rounded-md bg-accent/40 p-3 text-sm space-y-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      Counter-offer from CrediKing
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div>
                        <div className="text-xs text-muted-foreground">Principal</div>
                        <div>{formatNaira(loan.counterPrincipal ?? 0)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Repay</div>
                        <div>{formatNaira(loan.counterRepay ?? 0)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Duration</div>
                        <div>
                          {formatDurationDays(loan.counterDurationDays ?? loan.durationDays)}
                        </div>
                      </div>
                    </div>
                    {loan.counterNote && (
                      <p className="text-muted-foreground">{loan.counterNote}</p>
                    )}
                    {counterError && (counterLoanId === loan.id || actionLoanId === loan.id) && (
                      <ErrorBanner message={counterError} />
                    )}
                    {counterLoanId === loan.id ? (
                      <div className="space-y-3 pt-1 border-t border-border/60">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">
                          Your counter
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                          <Field label="Principal (NGN)">
                            <Input
                              type="number"
                              value={counterPrincipal}
                              onChange={(e) => setCounterPrincipal(e.target.value)}
                              inputMode="numeric"
                            />
                          </Field>
                          <Field label="Repay (NGN)">
                            <Input
                              type="number"
                              value={counterRepay}
                              onChange={(e) => setCounterRepay(e.target.value)}
                              inputMode="numeric"
                            />
                          </Field>
                          <Field label={`Days (${MIN_LOAN_DAYS}–${maxLoanDaysForLevel(b.verificationLevel)})`}>
                            <Input
                              type="number"
                              min={MIN_LOAN_DAYS}
                              max={maxLoanDaysForLevel(b.verificationLevel)}
                              value={counterDays}
                              onChange={(e) => setCounterDays(e.target.value)}
                              inputMode="numeric"
                            />
                          </Field>
                        </div>
                        <Field label="Note (optional)">
                          <Input
                            value={counterNote}
                            onChange={(e) => setCounterNote(e.target.value)}
                            placeholder="Optional message"
                          />
                        </Field>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            variant="gold"
                            loading={actionLoanId === loan.id}
                            onClick={() => submitBorrowerCounter(loan)}
                          >
                            Send counter
                          </Button>
                          <Button variant="ghost" onClick={() => setCounterLoanId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-3 pt-1">
                        <Button
                          variant="gold"
                          loading={actionLoanId === loan.id}
                          onClick={() => acceptCounter(loan)}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          disabled={actionLoanId === loan.id}
                          onClick={() => openBorrowerCounter(loan)}
                        >
                          Counter back
                        </Button>
                        <Button
                          variant="ghost"
                          loading={actionLoanId === loan.id}
                          onClick={() => declineCounter(loan)}
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {loan.status === "pending" && loan.counterNote && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Your counter was sent. Waiting for CrediKing to respond.
                    {loan.counterNote ? ` Note: ${loan.counterNote}` : ""}
                  </p>
                )}

                {loan.status === "counter_accepted" && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    You accepted the counter-offer. Waiting for final admin confirmation and
                    disbursement.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {active && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-xl">Repayment</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Pay any amount anytime before the due date — not only on the due date.
              </p>
            </div>
            <Button variant="gold" asChild>
              <Link to="/borrower/repay">Pay any amount</Link>
            </Button>
          </div>
          {active.installments.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4">#</th>
                    <th className="py-2 pr-4">Due by</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {active.installments.map((inst, i) => {
                    const overdue = !inst.paid && new Date(inst.dueDate).getTime() < Date.now();
                    return (
                      <tr key={inst.id} className="border-b border-border/40">
                        <td className="py-3 pr-4 text-muted-foreground">{i + 1}</td>
                        <td className="py-3 pr-4">{new Date(inst.dueDate).toLocaleDateString()}</td>
                        <td className="py-3 pr-4">{formatNaira(inst.amount)}</td>
                        <td className="py-3 pr-4">
                          <Badge tone={inst.paid ? "success" : overdue ? "danger" : "gold"}>
                            {inst.paid ? "Paid" : overdue ? "Overdue" : "Payable now"}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4 text-right">
                          {!inst.paid && (
                            <Button
                              variant="gold"
                              loading={payingId === inst.id}
                              onClick={() =>
                                payInstallment(
                                  active.id,
                                  Math.max(100, Math.min(inst.amount, totalDue(active) - active.paid)),
                                  inst.id,
                                )
                              }
                            >
                              Pay
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              Outstanding {formatNaira(totalDue(active) - active.paid)}. Use{" "}
              <Link to="/borrower/repay" className="text-gold hover:underline">
                Pay any amount
              </Link>{" "}
              to repay in full or in parts.
            </p>
          )}
        </Card>
      )}

      <Card>
        <h2 className="font-display text-xl">Loan history</h2>
        {loans.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No loans yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4">Loan</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Repay</th>
                  <th className="py-2 pr-4">Duration</th>
                  <th className="py-2 pr-4">Paid</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((l) => (
                  <tr key={l.id} className="border-b border-border/40">
                    <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
                      {l.id.slice(0, 8).toUpperCase()}
                    </td>
                    <td className="py-3 pr-4">{formatNaira(l.amount)}</td>
                    <td className="py-3 pr-4">{formatNaira(totalDue(l))}</td>
                    <td className="py-3 pr-4">{formatDurationDays(l.durationDays, { capAt30: false })}</td>
                    <td className="py-3 pr-4">{formatNaira(l.paid)}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={statusTone(l.status)}>{statusLabel(l.status)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function KycWelcomeModal({ onStart, onLater }: { onStart: () => void; onLater: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-navy-deep/70 backdrop-blur-sm p-4">
      <Card className="w-full max-w-md">
        <Badge tone="warning">Welcome</Badge>
        <h2 className="font-display text-2xl mt-2">Complete your KYC?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Verify your identity to unlock loans. You can do this now or later from your dashboard.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-end">
          <Button variant="ghost" onClick={onLater}>
            Later
          </Button>
          <Button variant="gold" asChild>
            <Link to="/borrower/apply" search={{ flow: "kyc" }} onClick={onStart}>
              Start KYC
            </Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}

function AddBankCard({ userId, onSaved }: { userId: string; onSaved: () => void }) {
  const { banks: nigerianBanks, loading: banksLoading } = useNigerianBanks();
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bankName = nigerianBanks.find((x) => x.code === bankCode)?.name ?? "";

  const onBlur = async () => {
    const digits = accountNumber.replace(/\D/g, "");
    if (digits.length < 10 || !bankCode) return;
    setVerifying(true);
    setError(null);
    try {
      const data = await verifyBankAccount(digits, bankCode);
      const name = data.account_name || data.data?.account_name || "";
      if (!name) throw new Error("Could not resolve account name.");
      setAccountName(name);
    } catch (e) {
      setAccountName("");
      setError(e instanceof Error ? e.message : "Bank verification failed.");
    } finally {
      setVerifying(false);
    }
  };

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await saveDisbursementBankAccount({
        userId,
        bankName,
        bankCode,
        accountNumber,
        accountName,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save bank account.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Badge tone="warning">Action required</Badge>
      <h2 className="font-display text-xl mt-2">Add disbursement bank account</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Your loan cannot be paid out until a verified bank account is on file.
      </p>
      <ErrorBanner message={error} />
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <Field label="Bank">
          <BankSelect
            banks={nigerianBanks}
            value={bankCode}
            loading={banksLoading}
            onChange={(code) => {
              setBankCode(code);
              setAccountName("");
            }}
          />
        </Field>
        <Field
          label="Account number"
          hint={verifying ? "Verifying…" : accountName ? `Account name: ${accountName}` : undefined}
        >
          <Input
            value={accountNumber}
            onChange={(e) => {
              setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
              setAccountName("");
            }}
            onBlur={onBlur}
            inputMode="numeric"
            placeholder="10 digits"
          />
        </Field>
      </div>
      <div className="mt-4">
        <Button variant="gold" loading={saving || verifying} onClick={save} disabled={!accountName}>
          Save bank account
        </Button>
      </div>
    </Card>
  );
}
